import clsx from 'clsx'
import Pane from 'components/Common/Pane'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import OfferPageGallery from '../OfferPageGallery'
import AlwaysOnRoomOptions from './AlwaysOnRoomOptions'

import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { clearBookingCalendar, fetchCalendars, fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import {
  fetchAvailableRatesForEachRoom,
  fetchAvailableRatesForOffer,
  fetchOfferFlightPrice,
  fetchRelatedTravelItems,
} from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import FlashHotelDesktopIndividualRooms from 'components/OfferPage/FlashHotel/FlashHotelDesktopIndividualRooms'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { replace } from 'connected-react-router'
import config from 'constants/config'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { urlHashDatePicker } from 'constants/url'
import { initialOfferPageRooms, OfferPageActions, OfferPageFilterType, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import { isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { getIsMultiRoomEnabled } from 'selectors/featuresSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import OfferAirportSelectBox from '../Filters/Airport/OfferAirportSelectBox'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferCalendarDatePickerSelectBox from '../Filters/CalendarPicker/OfferCalendarDatePickerSelectBox'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferCapacityWithAgesSelectBox from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelectBox'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import OfferIntro from './AlwaysOnIntro'
import AlwaysOnSidebarFilters from './AlwaysOnSidebarFilters'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import AlwaysOnStickyHeader from './AlwaysOnStickyHeader'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import OfferYmalStickyFooter from 'components/Recommendations/YouMayAlsoLike/Offer/OfferYmalStickyFooter'
import { InViewHookResponse, useInView } from 'react-intersection-observer'
import AlwaysOnAndLMEOfferPageStickyFooter from './AlwaysOnAndLMEOfferPageStickyFooter'
import OfferPageFlightDealBanner from '../OfferPageFlightDealBanner'
import ModalProvider from 'contexts/ModalProvider'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'

import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import useShareModalConfig from 'hooks/useShareModalConfig'
import moment from 'moment'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import AlwaysOnRoomOptionsMobile from './MobileBookingFlow/AlwaysOnRoomOptionsMobile'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { getCheapestSelectedPackageOptionWithDefault, getCheckoutDate, isAvailableForPurchase, offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import { useLocation } from 'react-router'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import AlwaysOnMobileStickyHeaderFilters from './MobileBookingFlow/AlwaysOnMobileStickyHeaderFilters'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'
import useHotelIndividualRoomAutoselection from 'hooks/OfferPage/useHotelIndividualRoomAutoselection'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { pluralizeToString } from 'lib/string/pluralize'
import MultiRoomModeToggle from '../MultipleRoomsBooking/MultiRoomModeToggle'
import HotelDesktopFlightToggle from '../Accommodation/HotelDesktopFlightToggle'
import OfferDesktopConnectingRoomsToggle from '../Filters/ConnectingRooms/OfferDesktopConnectingRoomsToggle'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

const RelativeBlock = styled.form`
  position: relative;
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledLayoutContainer = styled(LayoutContainer)`
  &.no-padding {
    padding: 0;
  }
`

const FlexDesktopFilterBlock = styled(DesktopFilterBlock)`
  flex-grow: 1;

  &:has(> :nth-child(3)) > * {
    flex: 1;
    max-width: unset;
  }

  ${mediaQueryDown.desktop} {
    &:has(> :nth-child(4)) > * {
      /* To ensure when they wrap we don't get three filters on the first row and then on on the second row */
      min-width: 34%;
    }
  }
`

interface Props {
  shouldUseLereStickyFooter: boolean
  lereYmalStickyFooterShowTrigger: InViewHookResponse
  offer: App.HotelOffer
  airport?: App.Airport
  duration: number
  activeFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  availableForPurchase?: boolean
  bestPackage: App.OfferPackageOptionView
  room?: App.PackageRoomType
  flightsEnabled?: boolean
  roomMode: OfferPageMultiRoomMode
  multipleRoomBookingActive: boolean
}

const currentTimer = {} as Record<string, any>

function slideTimer(name: string, callback: () => void, ms: number) {
  if (currentTimer[name]) {
    clearTimeout(currentTimer[name])
  }
  currentTimer[name] = setTimeout(callback, ms)
}

function AlwaysOnContent(props: Props) {
  const {
    shouldUseLereStickyFooter,
    lereYmalStickyFooterShowTrigger,
    offer,
    airport,
    duration,
    activeFilter,
    rooms,
    checkInDate,
    checkOutDate,
    availableForPurchase,
    bestPackage,
    activeHeaderFilter,
    flightsEnabled,
    roomMode,
    multipleRoomBookingActive,
  } = props

  const location = useLocation()
  const query = useQsQueryParams<{ roomRateId?: string, roomTypeId?: string }>()
  const { originAirports, airports } = useContext(GeoContext)
  const isMultiRoomEnabled = useAppSelector(state => getIsMultiRoomEnabled(state, offer))
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!checkInDate)

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()
  const { pricing, pricingWithFlights } = bestPackage
  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent && !offer.bundledWithFlightsOnly) || (offer.bundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  const loggedOutSavedPercent = showDiscountPill ? offer.bundledWithFlightsOnly ? pricingWithFlights?.discountPercent : pricing.discountPercent : undefined

  const scrollAfterFilterClose = useRef<boolean>(false)
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.Occupants> | null>(null)
  const lastFetchedAirport = useRef<App.Airport | null>(null)
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)

  const isMobileDevice = useScreenSizeOnly('mobile')
  const isNewMobileBookingFlowEnabled = useOptimizelyExperiment(OptimizelyExperiments.croAlwaysOnNewMobileBooking, isMobileDevice)
  const useNewMobileBookingFlow = isMobileDevice && isNewMobileBookingFlowEnabled

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const queryString = useMemo(() =>
    encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
      multiRoomMode: roomMode,
    }).toString(), [checkInDate, checkOutDate, rooms, roomMode, location.search])

  const canUseMultiRoomUI = isMultiRoomEnabled && rooms.length > 1

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  useIdleTimeReporter(IdleTimeSource.AlwaysOn)

  const fetchCalendarData = useCallback(() => {
    if (!offer.packages.length || !isValidOccupancy(...rooms)) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }

    if (canUseMultiRoomUI) {
      dispatch(fetchCalendarsByOccupancy(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    } else {
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: (offer.lowestPricePackage ? [offer.lowestPricePackage] : []),
      }))
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    }
    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport ?? null
  }, [airport, canUseMultiRoomUI, dispatch, duration, offer, rooms])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const hasRoomsChanged = useMemo(() => {
    const currentOccupancyNumber = rooms.map(generateOccupancyStringByRoom).join('|')
    const lastOccupancyNumber = (lastFetchedRooms.current ?? initialOfferPageRooms).map(generateOccupancyStringByRoom).join('|')
    return currentOccupancyNumber !== lastOccupancyNumber
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, activeFilter])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    if (previousFilter === 'capacity' && !hasRooms) {
      if (!activeFilter) {
        autoScrollToFilters()
        scrollAfterFilterClose.current = false
      }
    }

    if ((activeFilter === 'checkin' || activeHeaderFilter === 'checkin') && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else if (viewedDates && lastFetchedAirport.current !== airport) {
      // Filter has just closed, so we're applying it now
      // rooms has changed, all loaded calendars are no longer valid as we load calendars for specific
      // rooms (availablity), so clear them and refetch
      dispatch(clearBookingCalendar())
      fetchCalendarData()
    } else if (hasRoomsChanged) {
      // if multiple room booking is available and we changed the rooms,
      // don't clear the previous data, just update for the new changed
      // rooms (is filtered inside fetchCalendarsByOccupancy action)
      if (!canUseMultiRoomUI) {
        dispatch(clearBookingCalendar())
      }
      slideTimer('fetchCalendarData', fetchCalendarData, 1000)
    }

    if (activeFilter) {
      autoScrollToFilters()
    }
  }, [activeFilter, activeHeaderFilter, previousFilter, hasRoomsChanged, canUseMultiRoomUI, fetchCalendarData]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (query.roomTypeId && query.roomRateId) {
      const roomOption = { roomTypeId: query.roomTypeId, roomRateId: query.roomRateId }
      stateDispatch({ type: OfferPageActions.setRoomOption, roomOption })
    }
  }, [stateDispatch, query.roomTypeId, query.roomRateId])

  useEffect(() => {
    const useDynamicCancellationPolicies = offer.property.useDynamicCancellationPolicies
    const occupanciesInvalid = rooms.some(item => item.childrenAge?.some(age => age < 0))
    if (checkInDate && checkOutDate && !occupanciesInvalid && !activeFilter && useDynamicCancellationPolicies) {
      if (canUseMultiRoomUI) {
        dispatch(fetchAvailableRatesForEachRoom(
          offer,
          {
            occupants: rooms,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            flightOrigin: airport?.code,
          },
        ))
      } else {
        dispatch(fetchAvailableRatesForOffer(
          offer,
          {
            occupants: rooms,
            checkIn: checkInDate,
            checkOut: checkOutDate,
            flightOrigin: airport?.code,
          },
        ))
      }
    }
  }, [duration, activeFilter, checkInDate, offer, rooms, airport, canUseMultiRoomUI, checkOutDate, dispatch])

  useEffect(() => {
    slideTimer('fetchRelatedTravelItems', () => {
      dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
    }, 2000)
  }, [offer, duration, checkInDate, dispatch, rooms, checkOutDate])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages, multipleRoomBookingActive])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    // effectively an 'on airport change' event
    if (airport) {
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  useHotelIndividualRoomAutoselection()

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate.capacities)
  }, [offer.packages])

  const isValidRoomOccupancy = offer.bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = flightsEnabled && destinationAirport &&
    (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) &&
    isValidRoomOccupancy

  // ------------- LERE sticky footer -------------------
  const recommendationFilters = useMemo(() => ({
    checkIn: checkInDate,
    checkOut: checkInDate ? moment(checkInDate).clone().add(duration, 'days').format(ISO_DATE_FORMAT) : undefined,
    rooms,
  }), [checkInDate, duration, rooms])

  const lereYmalStickyFooterHideTrigger = useInView({ triggerOnce: false })

  const canShowYmalFooter = lereYmalStickyFooterShowTrigger.inView && !lereYmalStickyFooterHideTrigger.inView
  const showDealBanner = airport && destinationAirport
  // ------------- END of LERE sticky footer -------------------

  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', '),
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  return <ProductPaletteProvider product={offer}>
    <ImmersiveSaveButtonPortal>
      <BookmarkButton offer={offer} />
    </ImmersiveSaveButtonPortal>
    <TripGuard>
      <SavePromptHotel offer={offer} />
    </TripGuard>
    <RelativeBlock onSubmit={(e) => e.preventDefault()}>
      {useNewMobileBookingFlow && availableForPurchase && <AccommodationOfferPageStickyHeaderMobile>
        <AlwaysOnMobileStickyHeaderFilters />
      </AccommodationOfferPageStickyHeaderMobile>}
      <div>
        <CSSBreakpoint min="tablet">
          <AlwaysOnStickyHeader
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />
        </CSSBreakpoint>
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations
              hideOffers={!!activeFilter}
            />
          </div>
        )}
        {availableForPurchase && (
          <WalledContent enforced={offer.walledGarden}>
            <Breakpoint max="mobile">
              <ModalProvider>
                <AlwaysOnSidebarFilters
                  onViewPackageOptions={scrollToPackageOptions}
                  capacities={occupancyLimits}
                />
              </ModalProvider>
            </Breakpoint>
          </WalledContent>
        )}
        <span ref={lereYmalStickyFooterHideTrigger.ref} />
        <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
        <Pane>
          <OfferIntro
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            shareConfig={shareConfig}
          />
        </Pane>
      </div>
      <ScrollAnchor ref={packageOptionsAnchor} />
      <SoftOfferPageWalledGarden>
        <span ref={lereYmalStickyFooterShowTrigger.ref} />
        <PackageOptionsPane id="Package-Options">
          <StyledLayoutContainer className={clsx({ 'no-padding': useNewMobileBookingFlow })}>
            <VerticalSpacer gap={32}>
              {availableForPurchase && (
                <WalledContent enforced={offer.walledGarden}>
                  <>
                    {!useNewMobileBookingFlow && (
                      <CSSBreakpoint max="mobile">
                        <Group direction="vertical" gap={8}>
                          <OfferCapacityWithAgesSelectBox />
                          <OfferCalendarDatePickerSelectBox />
                          {showFlightInformation && <OfferAirportSelectBox />}
                        </Group>
                      </CSSBreakpoint>
                    )}
                    <CSSBreakpoint min="tablet">
                      <VerticalSpacer gap={16}>
                        <div>
                          <Heading variant="heading3" as="h2">
                            Select your {rooms.length > 1 ? pluralizeToString(offer.saleUnit, rooms.length) : offer.saleUnit}
                          </Heading>
                          {rooms.length > 1 && <BodyText variant="medium">
                            Choose the same package for all rooms, or customise each room individually.
                          </BodyText>}
                        </div>
                        {canUseMultiRoomUI && <Group direction="horizontal"><MultiRoomModeToggle inputsName="room-mode-desktop" /></Group>}
                        <div>
                          <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                          <Group ref={desktopFiltersWrapperRef} fullWidth direction="horizontal" gap={8} horizontalAlign="space-between" wrap="wrap">
                            <FlexDesktopFilterBlock direction="horizontal" gap={8} horizontalAlign="center" desktopHorizontalAlign="end" wrap="wrap" className={clsx({ open: !!activeFilter })}>
                              <OfferCapacityWithAgesSelect boundaryRef={desktopFiltersWrapperRef} capacities={occupancyLimits} />
                              <OfferCalendarDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                              {rooms.length > 1 && <OfferDesktopConnectingRoomsToggle />}
                              {flightsEnabled && <HotelDesktopFlightToggle />}
                              <Backdrop visible={!!activeFilter && !isMobileDevice} data-testid="filter-backdrop" />
                            </FlexDesktopFilterBlock>
                          </Group>
                        </div>
                      </VerticalSpacer>
                    </CSSBreakpoint>
                  </>
                </WalledContent>
              )}

              {!useNewMobileBookingFlow && (
                <VerticalSpacer gap={8}>
                  <BodyText variant="large" align="center">
                    Select duration
                  </BodyText>
                  <OfferDurationTabs />
                </VerticalSpacer>
              )}

              <LayoutContainer>
                {showDealBanner && config.FLIGHT_ENABLED && !isAgentHubEnabled && <OfferPageFlightDealBanner
                  destinationAirportCode={destinationAirport.code}
                  originAirportCode={airport.code}
                />}
              </LayoutContainer>

              {!canUseMultiRoomUI && offer.packages.length > 0 && <>
                {!useNewMobileBookingFlow && (
                  <AlwaysOnRoomOptions
                    showFlightInformation={showFlightInformation}
                    destinationAirport={destinationAirport}
                    onViewPackageOptions={scrollToPackageOptions}
                  />
                )}
                {useNewMobileBookingFlow && (
                  <AlwaysOnRoomOptionsMobile
                    showFlightInformation={showFlightInformation}
                  />
                )}
              </>}
              {canUseMultiRoomUI && (
                <FlashHotelDesktopIndividualRooms />
              )}
            </VerticalSpacer>
          </StyledLayoutContainer>
        </PackageOptionsPane>

        {useNewMobileBookingFlow && availableForPurchase && <AccommodationOfferPageStickyFooter />}
        {!useNewMobileBookingFlow && availableForPurchase && (!shouldUseLereStickyFooter || !canShowYmalFooter) &&
          <AlwaysOnAndLMEOfferPageStickyFooter
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
          />}
      </SoftOfferPageWalledGarden>
    </RelativeBlock>
    <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    {shouldUseLereStickyFooter && !useNewMobileBookingFlow && (
      <ErrorBoundary fallback={null}>
        <OfferYmalStickyFooter
          seedOffer={offer}
          filters={recommendationFilters}
          show={canShowYmalFooter}
          initialShowDelayMs={2000}
          hideImmediately={!!activeFilter}
          pageSource="AlwaysOn"
        />
      </ErrorBoundary>
    )}
  </ProductPaletteProvider>
}

type MappedProps = Pick<Props,
  | 'airport'
  | 'duration'
  | 'activeFilter'
  | 'rooms'
  | 'checkInDate'
  | 'checkOutDate'
  | 'availableForPurchase'
  | 'bestPackage'
  | 'room'
  | 'activeHeaderFilter'
  | 'flightsEnabled'
  | 'roomMode'
  | 'multipleRoomBookingActive'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  room: state.room,
  flightsEnabled: offerFlightsEnabled(state, appState),
  availableForPurchase: isAvailableForPurchase(state, appState),
  bestPackage: getCheapestSelectedPackageOptionWithDefault(appState, state),
  activeHeaderFilter: state.activeHeaderFilter,
  roomMode: state.multiRoomMode,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
}), AlwaysOnContent)
